import { apiRequest } from './client'
import { trackDraftWrite } from './draftWrites'

export interface DraftPrompt {
  filename: string
  content: string
}

export interface DraftPromptListEntry {
  filename: string
  existsInDraft: boolean
  lastModified?: string
}

interface RawDraftPromptListEntry {
  filename: string
  exists_in_draft: boolean
  last_modified?: string
}

// PRD 29's {filename} path param is flat (validated server-side against
// ^[A-Za-z0-9_-]+\.txt$ - no "/" at all, since the route is already scoped
// under prompts/). Every filename this app derives itself (deriveProduct
// BaselinePromptFilename, deriveDocumentPromptFilename) already matches that.
// extractors.json's own pre-existing prompt_template_file field predates PRD
// 29, though, and stores it prompts/-prefixed (e.g.
// "prompts/bedrock_vision_default.txt") - confirmed against real fixture
// data. Strip that prefix here, once, centrally, rather than at every call
// site, so every caller can pass a filename through unchanged regardless of
// which convention its source field happens to use.
export function normalizeFilename(filename: string): string {
  return filename.replace(/^\/?prompts\//, '')
}

// GET /gui/v1/drafts/current/prompts/{filename} (PRD 29) - draft copy if one
// exists, else the live bundle's copy (same copy-on-write semantics as
// getDraftFile).
export function getDraftPrompt(filename: string): Promise<DraftPrompt> {
  return apiRequest<DraftPrompt>(`/gui/v1/drafts/current/prompts/${normalizeFilename(filename)}`)
}

// PUT /gui/v1/drafts/current/prompts/{filename} (PRD 29) -> 204. The backend
// rejects empty content (GUI_005_INVALID_VALIDATE_REQUEST) - callers must
// never invoke this with an empty/whitespace-only string; clearing a prompt
// goes through deleteDraftPrompt instead.
export async function putDraftPrompt(filename: string, content: string): Promise<void> {
  await trackDraftWrite(
    apiRequest<void>(`/gui/v1/drafts/current/prompts/${normalizeFilename(filename)}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }),
  )
}

// DELETE /gui/v1/drafts/current/prompts/{filename} (PRD 29) -> 204;
// idempotent no-op if no draft edit exists.
export async function deleteDraftPrompt(filename: string): Promise<void> {
  await apiRequest<void>(`/gui/v1/drafts/current/prompts/${normalizeFilename(filename)}`, { method: 'DELETE' })
}

// GET /gui/v1/drafts/current/prompts (PRD 29) - every prompt filename that
// exists on either side (draft or live), flat, already in the same
// unprefixed form the other three functions here expect - used by the
// Prompts screen to surface files with real content but no current
// product/document type pointing at them ("orphaned" rows).
export async function getDraftPrompts(): Promise<DraftPromptListEntry[]> {
  const raw = await apiRequest<RawDraftPromptListEntry[]>('/gui/v1/drafts/current/prompts')
  return raw.map((entry) => ({
    filename: entry.filename,
    existsInDraft: entry.exists_in_draft,
    lastModified: entry.last_modified,
  }))
}
